import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../../hooks/useAuthStore';
import api from '../../services/api';
import '../../styles/settings.css';

function encryptPassword(password) {
  return btoa(unescape(encodeURIComponent(password)));
}

export default function AdminPanel() {
  const { user, updateUser, logout } = useAuthStore();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [packs, setPacks] = useState([]);
  const [stickerPack, setStickerPack] = useState('');
  const [stickerEmoji, setStickerEmoji] = useState('');
  const [stickerFile, setStickerFile] = useState(null);
  const [stickerBusy, setStickerBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [needChangePassword, setNeedChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdError, setPwdError] = useState('');

  const load = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/stats'),
      ]);
      setUsers(usersRes.data || []);
      setStats(statsRes.data);
      setError('');
    } catch (e) {
      setError('Не удалось загрузить данные');
    }
  };

  useEffect(() => {
    if (user?.mustChangePassword) setNeedChangePassword(true);
  }, [user?.mustChangePassword]);

  useEffect(() => { load(); }, []);

  const loadPacks = async () => {
    try {
      const res = await api.get('/stickers/packs');
      setPacks(res.data || []);
    } catch (e) {}
  };

  const addSticker = async () => {
    if (!stickerPack || !stickerFile || stickerBusy) return;
    setStickerBusy(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', stickerFile);
      if (stickerEmoji.trim()) formData.append('emoji', stickerEmoji.trim());
      await api.post(`/stickers/packs/${stickerPack}/stickers`, formData);
      setStickerFile(null);
      setStickerEmoji('');
      document.getElementById('sticker-file-input').value = '';
      await loadPacks();
      setMsg('Стикер добавлен');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось добавить стикер');
    }
    setStickerBusy(false);
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 8) { setPwdError('Минимум 8 символов'); return; }
    if (newPassword !== confirmPassword) { setPwdError('Пароли не совпадают'); return; }
    setPwdBusy(true);
    setPwdError('');
    try {
      await api.put('/users/password', { password: encryptPassword(newPassword) });
      updateUser({ mustChangePassword: false });
      setNeedChangePassword(false);
      setNewPassword('');
      setConfirmPassword('');
      setMsg('Пароль изменён');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setPwdError('Не удалось сменить пароль');
    }
    setPwdBusy(false);
  };

  const setBlocked = async (id, blocked) => {
    if (!window.confirm(blocked ? 'Заблокировать пользователя?' : 'Разблокировать пользователя?')) return;
    setBusyId(id);
    setError('');
    try {
      await api.put(`/admin/users/${id}/block`, { blocked });
      setUsers((s) => s.map((u) => (u.id === id ? { ...u, isBlocked: blocked } : u)));
    } catch (e) {
      setError(e.response?.data?.message || 'Ошибка');
    }
    setBusyId(null);
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Удалить пользователя? Аккаунт будет заблокирован и обезличен (восстановление невозможно).')) return;
    setBusyId(id);
    setError('');
    try {
      await api.delete(`/admin/users/${id}`);
      setUsers((s) => s.filter((u) => u.id !== id));
    } catch (e) {
      setError(e.response?.data?.message || 'Ошибка');
    }
    setBusyId(null);
  };

  if (needChangePassword) {
    return (
      <div className="admin-panel">
        <div className="admin-header">
          <h2>🔐 Смена пароля администратора</h2>
          <button className="settings-close-btn" onClick={logout}>Выйти</button>
        </div>
        <div className="admin-card">
          <p style={{ fontSize: '13px', color: 'var(--system-text, #666)' }}>
            При первом входе необходимо сменить временный пароль администратора.
          </p>
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label>Новый пароль</label>
            <input type="password" value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setPwdError(''); }} />
          </div>
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label>Повторите пароль</label>
            <input type="password" value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setPwdError(''); }} />
          </div>
          {pwdError && <div className="settings-error" style={{ color: '#e74c3c', marginBottom: '10px' }}>{pwdError}</div>}
          <button className="settings-btn primary" onClick={handleChangePassword} disabled={pwdBusy}>
            {pwdBusy ? 'Сохранение...' : 'Сменить пароль'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>🛡 Администрирование</h2>
        <span style={{ fontSize: '12px', color: 'var(--system-text, #888)' }}>Вы вошли как: {user?.email}</span>
        <button className="settings-close-btn" onClick={logout}>Выйти</button>
      </div>
      <div className="admin-tabs">
        <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}>Пользователи</button>
        <button className={tab === 'stats' ? 'active' : ''} onClick={() => setTab('stats')}>Статистика</button>
        <button className={tab === 'stickers' ? 'active' : ''} onClick={() => { setTab('stickers'); loadPacks(); }}>Стикеры</button>
      </div>
      {error && <div className="settings-error" style={{ color: '#e74c3c', padding: '8px 0' }}>{error}</div>}
      {msg && <div className="settings-error" style={{ color: '#4CAF50', padding: '8px 0' }}>{msg}</div>}

      {tab === 'users' && (
        <div className="admin-users">
          {users.map((u) => (
            <div key={u.id} className={`admin-user-row ${u.isBlocked ? 'blocked' : ''}`}>
              <div className="admin-user-info">
                <span className="admin-user-login">{u.login}</span>
                <span className="admin-user-meta">
                  {u.isAdmin ? 'администратор' : ''} · создан: {String(u.createdAt || '').slice(0, 10)}
                  {u.isBlocked ? ' · 🔒 заблокирован' : ''}
                </span>
              </div>
              <div className="admin-user-actions">
                {!u.isAdmin && (
                  <>
                    <button className="action-btn admin-btn"
                      onClick={() => setBlocked(u.id, !u.isBlocked)} disabled={busyId === u.id}>
                      {u.isBlocked ? '🔓 Разблокировать' : '🔒 Заблокировать'}
                    </button>
                    <button className="action-btn admin-btn danger"
                      onClick={() => deleteUser(u.id)} disabled={busyId === u.id}>
                      Удалить
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'stats' && stats && (
        <div className="admin-stats">
          <div className="admin-stats-cards">
            <div className="admin-stat-card">
              <span className="admin-stat-value">{stats.totalUsers}</span>
              <span className="admin-stat-label">Всего пользователей</span>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-value">{stats.totalAiRequests}</span>
              <span className="admin-stat-label">Запросов к ИИ (30 дней)</span>
            </div>
          </div>
          <div className="admin-chart-section">
            <h4>Новые пользователи по дням (30 дней)</h4>
            <div className="admin-bars">
              {stats.newUsersByDay.map((d) => (
                <div key={d.date} className="admin-bar-col" title={`${d.date}: ${d.count}`}>
                  <div className="admin-bar" style={{ height: `${Math.max((d.count / (Math.max(...stats.newUsersByDay.map((x) => x.count), 1))) * 60, 2)}px` }} />
                  <span className="admin-bar-label">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="admin-chart-section">
            <h4>Запросы генерации картинок ИИ по дням (30 дней)</h4>
            <div className="admin-bars">
              {stats.aiRequestsByDay.map((d) => (
                <div key={d.date} className="admin-bar-col" title={`${d.date}: ${d.count}`}>
                  <div className="admin-bar ai" style={{ height: `${Math.max((d.count / (Math.max(...stats.aiRequestsByDay.map((x) => x.count), 1))) * 60, 2)}px` }} />
                  <span className="admin-bar-label">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    {tab === 'stickers' && (
        <div className="admin-card">
          <h4 style={{ margin: '0 0 12px' }}>Добавление стикера в паки</h4>
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label>Стикерпак</label>
            <select value={stickerPack} onChange={(e) => setStickerPack(e.target.value)}>
              <option value="">— выберите паки —</option>
              {packs.map((p) => (
                <option key={p.id} value={p.id}>{p.title} ({p.stickerCount})</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label>Файл стикера (PNG/WebP/анимированный WebP)</label>
            <input
              id="sticker-file-input"
              type="file"
              accept="image/png,image/webp"
              onChange={(e) => setStickerFile(e.target.files?.[0] || null)}
            />
          </div>
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <label>Эмодзи (необязательно)</label>
            <input type="text" value={stickerEmoji} onChange={(e) => setStickerEmoji(e.target.value)} maxLength={10} />
          </div>
          <button className="settings-btn primary" onClick={addSticker} disabled={stickerBusy || !stickerPack || !stickerFile}>
            {stickerBusy ? 'Загрузка...' : 'Добавить стикер'}
          </button>
          <p style={{ fontSize: '12px', color: 'var(--system-text, #888)', marginTop: '10px' }}>
            Стикер сохраняется в файловое хранилище (MinIO) и сразу появляется в паки у всех пользователей.
          </p>
        </div>
      )}
    </div>
  );
}